'use client';

import { useForm, useWatch } from 'react-hook-form';
import { useRouter } from '@/i18n/routing';
import { useTranslations } from 'next-intl';
import { zodResolver } from '@hookform/resolvers/zod';
import { pagesRoutes } from '@/shared/constants/routes';
import {
  createAccountFormSchema,
  type CreateAccountFormValues,
} from '@/features/auth/internal/schemas/create-account-form.schema';
import { buildL18nErrorCode } from '@/features/auth/internal/helpers/build-l18n-error-code';
import { AxiosError } from 'axios';
import { isClientFacingErrorContent } from '@/shared/is-client-facing-error-content';
import { signUpUser } from '@/features/auth/internal/helpers/browser-clients/signup';
import { setClientCookie } from '@/shared/utils/client-cookies';
import { VERIFICATION_EMAIL_COOKIE } from '@/features/auth/shared/helpers/constants/verification-cookies';
import { emailVerificationConfig } from '@/features/auth/internal/helpers/configs/email-verification';

export interface UseCreateAccountOptions {
  campaignId?: string | null;
}

export const useCreateAccount = ({ campaignId = null }: UseCreateAccountOptions = {}) => {
  const t = useTranslations('Auth.createAccount');
  const router = useRouter();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<CreateAccountFormValues>({
    resolver: zodResolver(createAccountFormSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      email: '',
      password: '',
      subscribeToNewsletter: true,
    },
  });

  const onSubmit = async (values: CreateAccountFormValues) => {
    try {
      // TODO: Pass marketing data for newsletter if opt in
      await signUpUser({
        email: values.email,
        password: values.password,
        confirmPassword: values.password,
        subscribeToNewsletter: values.subscribeToNewsletter,
        campaignId: campaignId ?? undefined,
      });

      setClientCookie(
        VERIFICATION_EMAIL_COOKIE,
        values.email,
        emailVerificationConfig.tokenTtlInSeconds,
      );
      const verifyEmailHref = campaignId
        ? `/get-started/${encodeURIComponent(campaignId)}/verify-email`
        : pagesRoutes.verifyEmail.href;
      router.push(verifyEmailHref);
    } catch (err: unknown) {
      if (err instanceof AxiosError && isClientFacingErrorContent(err.response?.data)) {
        const msg = err.response.data.l18nMessage;
        const type = err.response.data.type ?? 'Unexpected';
        setError('root', {
          message: t(buildL18nErrorCode(msg)),
          type,
        });
        return;
      }
      setError('root', { message: t(buildL18nErrorCode('unexpectedError')) });
    }
  };

  const subscribeToNewsletter = useWatch({ control, name: 'subscribeToNewsletter' });
  const password = useWatch({ control, name: 'password' });

  return {
    register,
    control,
    handleSubmit,
    errors,
    isSubmitting,
    onSubmit,
    subscribeToNewsletter,
    password,
  };
};
